/* ═══════════════════════════════════════════════════════════════
   People — Directory (Members + Teams merged) and Org Chart.
   Clean-sheet replacement for the old Teams section
   (pages/team/TeamPage.tsx), built to docs/PEOPLE_REBUILD_PLAN.md.
   Shell: tabs, ALL data loading (loadAll ported verbatim from the
   old TeamPage), ALL mutation handlers, one ConfirmDialog instance.

   Behavior preserved from the old TeamPage/MembersTab/TeamsTab/
   MemberEditor/TeamEditor (docs/PEOPLE_REBUILD_INVENTORY.md) with
   these Josh-granted functional changes only:
     1. Name split — firstName/middleName/lastName (+ `name` stays
        the maintained display column server-side).
     2. Deactivate cascade — removes from all teams + the org chart;
        Active-checkbox → status chip + Deactivate…/Reactivate.
     3. Members + Teams merge — one Directory surface; team
        assignment happens FROM the person (PersonPanel) as well as
        from the team tree + TeamEditorPanel.
     4. Secondary email per person (member_profiles side table).
   Team Management UX Overhaul (2026-08-18): the Teams modal
   (TeamsPanel.tsx, deleted) and the Add Member top bar
   (AddMemberForm.tsx, deleted) both moved into right-side panels —
   TeamEditorPanel.tsx and AddMemberPanel.tsx — sharing PersonPanel's
   slot (single-occupancy, "side panels are THE pattern for People").
   Team selection now happens via the Directory team tree
   (TeamTree.tsx), which also carries drag-and-drop: person row onto
   a team assigns them, team onto team nests/reparents, team onto the
   tree's root area un-nests.
   Org Chart tab mounts OrgChartV2 (pages/people/orgchart/).
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Copy } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { TeamService } from '../../services/teamService';
import { listCached } from '../../services/cloudCache';
import { ProjectService } from '../../services/projectService';
import { AdminService } from '../../services/adminService';
import { InvitationService } from '../../services/invitationService';
import type {
  TeamMember,
  Team,
  TeamAssignment,
  ProjectAssignment,
  Project,
  Role,
  BillingRole,
  Invitation,
} from '../../../shared/types';
import type {
  Department,
  OrgChartEdge,
  OrgChartJunction,
  OrgChartNote,
  OrgChartView,
  OrgChartViewPosition,
} from '../../../shared/entities';
import { OrgChartEdgeService } from '../../services/orgChartEdgeService';
import { OrgChartJunctionService } from '../../services/orgChartJunctionService';
import { OrgChartNoteService } from '../../services/orgChartNoteService';
import { OrgChartViewService } from '../../services/orgChartViewService';
import { OrgChartViewPositionService } from '../../services/orgChartViewPositionService';
import { DepartmentService } from '../../services/departmentService';
import type { AuthUser } from '../../services/authService';
import { hasPermission } from '../../../shared/permissions';
import { TEAM_COLORS } from '../../lib/palette';
import { DirectoryTab, type DirectoryActivePanel } from './DirectoryTab';
import { OrgChartV2 } from './orgchart/OrgChartV2';
import { toAddress } from '../../routing/address';
import { useAddressSync } from '../../routing/useAddressSync';
import { PlusIcon, displayName, combineName } from './styles';

interface PeoplePageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  /** One-shot drill from another page: open the person panel for memberId. */
  deepLink?: { view: 'team'; memberId?: string } | null;
  onDeepLinkConsumed?: () => void;
  /** No-work-loss hardening (2026-08-19): mirrors the Org Chart edit
   *  session's dirty state up to the shell so window close/refresh can
   *  warn before discarding it (the app-wide `beforeunload` guard already
   *  does this for the spec editor via `editorDirtyRef` — this is the
   *  same pattern for the org chart). Optional — omitted callers just
   *  don't get the close-tab warning, same fail-open default as every
   *  other optional callback here. */
  onDirtyChange?: (dirty: boolean) => void;
}

type ActiveTab = 'directory' | 'orgchart';

// ─── Empty factories ───

function emptyTeam(): Team {
  return {
    id: '',
    name: '',
    description: '',
    color: TEAM_COLORS[Math.floor(Math.random() * TEAM_COLORS.length)],
    createdAt: '',
    updatedAt: '',
  };
}

// Confirm-dialog flows funneled through one dialog instance instead of a
// scattered window.confirm() per action (docs/DESIGN.md §5 kit confirm).
type ConfirmState =
  | { kind: 'discardMember' }
  | { kind: 'discardTeam' }
  | { kind: 'removeMember'; id: string; hard: boolean }
  | { kind: 'deleteTeam'; id: string }
  | null;

// The Directory's right-side panel slot is single-occupancy (Team
// Management UX Overhaul, 2026-08-18: "side panels are THE pattern for
// People" — PersonPanel / TeamEditorPanel / AddMemberPanel never show at
// once). Switching panels while the CURRENTLY open one has unsaved changes
// funnels through the same discard confirm used for within-type switches;
// this records what to open once the user confirms.
type PendingOpen =
  | { kind: 'member'; member: TeamMember }
  | { kind: 'team'; team: Team }
  | { kind: 'addMember' }
  | null;

// ─── Main Page ───

function PeoplePage({
  toast,
  authUser,
  deepLink,
  onDeepLinkConsumed,
  onDirtyChange: onShellDirtyChange,
}: PeoplePageProps) {
  const [tab, setTab] = useState<ActiveTab>('directory');
  const [pendingOpenMemberId, setPendingOpenMemberId] = useState<string | null>(null);

  // No-work-loss hardening (2026-08-19): switching tabs used to unmount
  // OrgChartV2 unconditionally, silently discarding an active edit
  // session's staged changes with zero warning — the likely explanation
  // for lost-work reports that a failed-Save retry doesn't cover (a
  // failed commit already correctly keeps the session open). Every
  // `setTab` call goes through this guarded wrapper instead.
  const orgChartDirtyRef = useRef(false);
  const blockedBackRef = useRef<{ proceed: () => void; reset: () => void } | null>(null);
  const handleOrgChartDirtyChange = useCallback(
    (dirty: boolean) => {
      orgChartDirtyRef.current = dirty;
      onShellDirtyChange?.(dirty);
    },
    [onShellDirtyChange]
  );
  const safeSetTab = useCallback(
    (next: ActiveTab) => {
      if (tab === 'orgchart' && next !== 'orgchart' && orgChartDirtyRef.current) {
        toast('Save or cancel your Org Chart changes before switching tabs', true);
        return;
      }
      setTab(next);
    },
    [tab, toast]
  );

  // Data — initialize from cache so pages never flash empty on remount
  const [members, setMembers] = useState<TeamMember[]>(() =>
    listCached<TeamMember>('team_members')
  );
  const [roles, setRoles] = useState<Role[]>(() => listCached<Role>('roles'));
  const [projects, setProjects] = useState<Project[]>(() => listCached<Project>('projects'));
  const [allAssignments, setAllAssignments] = useState<ProjectAssignment[]>(() =>
    listCached<ProjectAssignment>('project_assignments')
  );
  const [teams, setTeams] = useState<Team[]>(() => listCached<Team>('teams'));
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>(() =>
    listCached<TeamAssignment>('team_assignments')
  );
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>(() =>
    listCached<BillingRole>('billing_roles')
  );
  const [departments, setDepartments] = useState<Department[]>(() =>
    listCached<Department>('departments')
  );
  const [orgChartEdges, setOrgChartEdges] = useState<OrgChartEdge[]>(() =>
    listCached<OrgChartEdge>('org_chart_edges')
  );
  const [orgChartJunctions, setOrgChartJunctions] = useState<OrgChartJunction[]>(() =>
    listCached<OrgChartJunction>('org_chart_junctions')
  );
  const [orgChartNotes, setOrgChartNotes] = useState<OrgChartNote[]>(() =>
    listCached<OrgChartNote>('org_chart_notes')
  );
  const [orgChartViews, setOrgChartViews] = useState<OrgChartView[]>(() =>
    listCached<OrgChartView>('org_chart_views')
  );
  const [orgChartViewPositions, setOrgChartViewPositions] = useState<OrgChartViewPosition[]>(() =>
    listCached<OrgChartViewPosition>('org_chart_view_positions')
  );
  const [invitations, setInvitations] = useState<Invitation[]>([]);

  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);

  // Invite / add-member form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteMiddleName, setInviteMiddleName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [inviteBillingRoleId, setInviteBillingRoleId] = useState('');

  // Single-occupancy right panel slot — 'person' | 'team' | 'addMember' | null.
  const [activePanel, setActivePanel] = useState<DirectoryActivePanel>(null);

  // Person panel (master-detail RIGHT side) state — replaces the old
  // MemberEditor modal's `editingMember`.
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [memberDirty, setMemberDirty] = useState(false);
  const [memberProjectAssignments, setMemberProjectAssignments] = useState<ProjectAssignment[]>([]);
  const [memberTeamAssignments, setMemberTeamAssignments] = useState<TeamAssignment[]>([]);
  const [addingProject, setAddingProject] = useState(false);

  // Team editor panel state — replaces the old Teams-modal's edit-in-place
  // half (TeamsPanel.tsx); team SELECTION now happens via the Directory
  // team tree instead of an in-panel list.
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamDirty, setTeamDirty] = useState(false);
  const [editingTeamAssignments, setEditingTeamAssignments] = useState<TeamAssignment[]>([]);
  const [addingTeamMember, setAddingTeamMember] = useState(false);

  // What to open once a pending discard confirm resolves — covers both a
  // different row/team being clicked while the CURRENT panel has unsaved
  // changes (master-detail form enables this; a modal never had to, since
  // it blocked the page underneath it) and switching panel TYPE.
  const [pendingOpen, setPendingOpen] = useState<PendingOpen>(null);

  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);
  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadAll(retry = true) {
    try {
      const [m, r, p, a, t, ta, br, inv, depts, oce, ocj, ocn, ocv, ocvp] =
        await Promise.allSettled([
          TeamService.list(),
          AdminService.listRoles(),
          ProjectService.list(),
          TeamService.listAssignments(),
          TeamService.listTeams(),
          TeamService.listTeamAssignments(),
          AdminService.billingRoles.list().catch(() => [] as any[]),
          InvitationService.list().catch(() => [] as Invitation[]),
          DepartmentService.list().catch(() => [] as Department[]),
          OrgChartEdgeService.list().catch(() => [] as OrgChartEdge[]),
          OrgChartJunctionService.list().catch(() => [] as OrgChartJunction[]),
          OrgChartNoteService.list().catch(() => [] as OrgChartNote[]),
          OrgChartViewService.list().catch(() => [] as OrgChartView[]),
          OrgChartViewPositionService.list().catch(() => [] as OrgChartViewPosition[]),
        ]);
      if (m.status === 'fulfilled') setMembers([...m.value]);
      else console.error('PeoplePage loadAll: members failed:', m.reason);
      if (r.status === 'fulfilled') setRoles([...r.value]);
      else console.error('PeoplePage loadAll: roles failed:', r.reason);
      if (p.status === 'fulfilled') setProjects([...p.value]);
      else console.error('PeoplePage loadAll: projects failed:', p.reason);
      if (a.status === 'fulfilled') setAllAssignments([...a.value]);
      else console.error('PeoplePage loadAll: assignments failed:', a.reason);
      if (t.status === 'fulfilled') setTeams([...t.value]);
      else console.error('PeoplePage loadAll: teams failed:', t.reason);
      if (ta.status === 'fulfilled') setTeamAssignments([...ta.value]);
      else console.error('PeoplePage loadAll: team assignments failed:', ta.reason);
      if (br.status === 'fulfilled') setBillingRoles([...br.value]);
      if (depts.status === 'fulfilled') setDepartments([...depts.value]);
      if (oce.status === 'fulfilled') setOrgChartEdges([...oce.value]);
      if (ocj.status === 'fulfilled') setOrgChartJunctions([...ocj.value]);
      if (ocn.status === 'fulfilled') setOrgChartNotes([...ocn.value]);
      if (ocv.status === 'fulfilled') setOrgChartViews([...ocv.value]);
      if (ocvp.status === 'fulfilled') setOrgChartViewPositions([...ocvp.value]);
      if (inv.status === 'fulfilled')
        setInvitations(inv.value.filter((i) => i.status === 'pending'));
      const anyFailed = [m, r, p, a, t, ta].some((res) => res.status === 'rejected');
      if (anyFailed && retry) {
        setTimeout(() => loadAll(false), 1500);
        return;
      }
      setStale(anyFailed);
      setLoading(false);
    } catch (e) {
      console.error('PeoplePage loadAll failed:', e);
      if (retry) {
        setTimeout(() => loadAll(false), 1500);
        return;
      }
      setStale(true);
      setLoading(false);
    }
  }

  // ─── Helpers ───

  const resolveRole = useCallback(
    (m: TeamMember) => {
      if (m.roleId) {
        const r = roles.find((rr) => rr.id === m.roleId);
        if (r) return r;
      }
      if (m.role) {
        const lowered = m.role.toLowerCase();
        return roles.find((rr) => rr.name?.toLowerCase() === lowered);
      }
      return undefined;
    },
    [roles]
  );

  const roleName = useCallback(
    (m: TeamMember): string => {
      const r = resolveRole(m);
      if (r) return r.name;
      return m.role || '';
    },
    [resolveRole]
  );

  const linkRoleId = useCallback(
    (m: TeamMember): TeamMember => {
      if (m.roleId) return m;
      const r = resolveRole(m);
      return r ? { ...m, roleId: r.id } : m;
    },
    [roles]
  );

  // Consume incoming deep-link once: switch to Directory and queue the
  // memberId to open as soon as `members` is loaded (handled below).
  useEffect(() => {
    if (!deepLink) return;
    safeSetTab('directory');
    if (deepLink.memberId) setPendingOpenMemberId(deepLink.memberId);
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  useEffect(() => {
    if (!pendingOpenMemberId || members.length === 0) return;
    const target = members.find((m) => m.id === pendingOpenMemberId);
    if (target) openPersonPanel(target);
    setPendingOpenMemberId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOpenMemberId, members]);

  const memberName = useCallback(
    (id?: string): string => {
      if (!id) return '';
      const m = members.find((m) => m.id === id);
      return m ? displayName(m) : '';
    },
    [members]
  );

  const teamName = useCallback(
    (id: string): string => {
      return teams.find((t) => t.id === id)?.name || '';
    },
    [teams]
  );

  // ─── Permission gating ───
  const canViewRoles =
    hasPermission(authUser?.permissions, 'admin_center.view') ||
    hasPermission(authUser?.permissions, 'admin_center.manage_roles');
  const canViewBilling = hasPermission(authUser?.permissions, 'admin_center.manage_billing');
  const canEditMembers = hasPermission(authUser?.permissions, 'team_members.edit');
  const canCreateMembers = hasPermission(authUser?.permissions, 'team_members.create');
  const canHardDeleteMembers = hasPermission(authUser?.permissions, 'admin_center.manage_roles');
  const canDeactivateMembers =
    hasPermission(authUser?.permissions, 'team_members.deactivate') || canHardDeleteMembers;
  const canRemoveMembers = canDeactivateMembers || canHardDeleteMembers;
  const canManageRoles = hasPermission(authUser?.permissions, 'team_members.manage_roles');

  // ─── Person panel actions ───

  async function openPersonPanel(m: TeamMember) {
    setActivePanel('person');
    setEditingMember(linkRoleId({ ...m }));
    setMemberDirty(false);
    setAddingProject(false);
    if (m.id) {
      const [pa, mta] = await Promise.allSettled([
        TeamService.listAssignments(m.id),
        TeamService.listTeamAssignments(undefined, m.id),
      ]);
      if (pa.status === 'fulfilled') setMemberProjectAssignments(pa.value);
      else console.error('openPersonPanel: project assignments failed:', pa.reason);
      if (mta.status === 'fulfilled') setMemberTeamAssignments(mta.value);
      else console.error('openPersonPanel: team assignments failed:', mta.reason);
    } else {
      setMemberProjectAssignments([]);
      setMemberTeamAssignments([]);
    }
  }

  /** Guarded entry point for opening the person panel (Directory row
   *  click) — routes through whichever panel is currently open first:
   *  same-type dirty (another member unsaved) or cross-type dirty (team
   *  editor unsaved) both funnel through the ONE discard confirm before
   *  switching the single right-panel slot. */
  function requestOpenMember(m: TeamMember) {
    if (activePanel === 'person' && editingMember?.id === m.id) return;
    if (activePanel === 'person' && memberDirty) {
      setPendingOpen({ kind: 'member', member: m });
      setConfirmState({ kind: 'discardMember' });
      return;
    }
    if (activePanel === 'team' && teamDirty) {
      setPendingOpen({ kind: 'member', member: m });
      setConfirmState({ kind: 'discardTeam' });
      return;
    }
    if (activePanel === 'team') closeTeamEditorForce();
    if (activePanel === 'addMember') closeAddMemberPanelForce();
    openPersonPanel(m);
  }

  function requestCloseMemberEditor() {
    if (memberDirty) setConfirmState({ kind: 'discardMember' });
    else closeMemberEditorForce();
  }
  function closeMemberEditorForce() {
    setEditingMember(null);
    setMemberDirty(false);
    setMemberProjectAssignments([]);
    setMemberTeamAssignments([]);
    setAddingProject(false);
    if (activePanel === 'person') setActivePanel(null);
  }

  function updateMember(updates: Partial<TeamMember>) {
    setEditingMember((prev) => (prev ? { ...prev, ...updates } : prev));
    setMemberDirty(true);
  }

  async function saveMember() {
    if (!editingMember) return;
    // First required — mirrors the old non-empty full-name validation
    // (People Rebuild grant #1: name split).
    if (!editingMember.firstName?.trim()) {
      toast('First name is required', true);
      return;
    }
    if (editingMember.roleId) {
      const r = roles.find((r) => r.id === editingMember.roleId);
      if (r) editingMember.role = r.name;
    }
    try {
      const saved = await TeamService.save(editingMember);
      await loadAll();
      setEditingMember(linkRoleId({ ...saved }));
      setMemberDirty(false);
      toast('Team member saved');
    } catch (err: any) {
      toast(err.message || 'Save failed', true);
    }
  }

  async function handleTeamAssign(memberId: string, teamId: string) {
    const existing = teamAssignments.filter((a) => a.teamMemberId === memberId);
    if (!existing.some((a) => a.teamId === teamId)) {
      await TeamService.assignToTeam(teamId, memberId, 'member');
    }
    await loadAll();
    const t = teams.find((t) => t.id === teamId);
    toast(`${memberName(memberId)} added to ${t?.name || 'team'}`);
  }

  async function handleTeamRemove(memberId: string, teamId: string) {
    const ta = teamAssignments.find((a) => a.teamMemberId === memberId && a.teamId === teamId);
    if (!ta) return;
    await TeamService.removeFromTeam(ta.id);
    await loadAll();
    const t = teams.find((t) => t.id === teamId);
    toast(`${memberName(memberId)} removed from ${t?.name || 'team'}`);
  }

  async function handleToggleTeamRole(memberId: string, teamId: string) {
    const ta = teamAssignments.find((a) => a.teamMemberId === memberId && a.teamId === teamId);
    if (!ta) return;
    const newRole = ta.teamRole === 'leader' ? 'member' : 'leader';
    await TeamService.updateTeamAssignment({ ...ta, teamRole: newRole });
    await loadAll();
    toast(`${memberName(memberId)} is now ${newRole}`);
  }

  // Wrappers bound to the selected person — used by PersonPanel's Teams
  // section (People Rebuild grant #3: team assignment FROM the person).
  // Reuse the exact same TeamService calls as the org chart's drag-drop
  // and the Teams secondary surface; additionally refresh the panel's own
  // memberTeamAssignments snapshot (loadAll only refreshes the page-wide
  // teamAssignments list).
  async function addTeamToMember(teamId: string) {
    if (!editingMember?.id) return;
    await handleTeamAssign(editingMember.id, teamId);
    setMemberTeamAssignments(await TeamService.listTeamAssignments(undefined, editingMember.id));
  }
  async function removeTeamFromMember(teamId: string) {
    if (!editingMember?.id) return;
    await handleTeamRemove(editingMember.id, teamId);
    setMemberTeamAssignments(await TeamService.listTeamAssignments(undefined, editingMember.id));
  }
  async function toggleMemberTeamRole(teamId: string) {
    if (!editingMember?.id) return;
    await handleToggleTeamRole(editingMember.id, teamId);
    setMemberTeamAssignments(await TeamService.listTeamAssignments(undefined, editingMember.id));
  }

  function requestDeactivate(id: string) {
    setConfirmState({ kind: 'removeMember', id, hard: false });
  }

  function requestHardDelete(id: string) {
    setConfirmState({ kind: 'removeMember', id, hard: true });
  }

  /** Plain-English summary of the deactivation cascade (People Rebuild
   *  grant #2: removes the person from every team and the org chart). */
  function describeCascade(cascade?: {
    removedAssignments: number;
    removedEdges: number;
    promotedChildren: number;
  }): string {
    if (!cascade) return 'Team member archived';
    const parts: string[] = [];
    if (cascade.removedAssignments > 0)
      parts.push(
        `${cascade.removedAssignments} team${cascade.removedAssignments !== 1 ? 's' : ''}`
      );
    if (cascade.removedEdges > 0)
      parts.push(
        `${cascade.removedEdges} chart connection${cascade.removedEdges !== 1 ? 's' : ''}`
      );
    if (cascade.promotedChildren > 0)
      parts.push(
        `${cascade.promotedChildren} direct report${cascade.promotedChildren !== 1 ? 's' : ''} reassigned`
      );
    if (parts.length === 0) return 'Team member archived';
    return `Team member archived — removed from ${parts.join(', ')}`;
  }

  async function performRemoveMember(id: string, hard: boolean) {
    setConfirmBusy(true);
    try {
      if (hard) {
        await TeamService.hardDelete(id);
        if (editingMember?.id === id) closeMemberEditorForce();
        await loadAll();
        toast('Team member deleted');
      } else {
        const result: any = await TeamService.deactivate(id);
        if (editingMember?.id === id) closeMemberEditorForce();
        await loadAll();
        toast(describeCascade(result?.cascade));
      }
      setConfirmState(null);
    } catch (err: any) {
      toast(err.message || 'Action failed', true);
    } finally {
      setConfirmBusy(false);
    }
  }

  /** Reactivate — a plain save({active:true}), the state-named flip button
   *  replacing the old "Active member" checkbox as the reactivation path
   *  (People Rebuild grant #2). */
  async function reactivateMember(id: string) {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    try {
      const saved = await TeamService.save({ ...member, active: true });
      if (editingMember?.id === id) setEditingMember(linkRoleId({ ...saved }));
      await loadAll();
      toast(`${displayName(member)} restored`);
    } catch (err: any) {
      toast(err.message || 'Failed to restore', true);
    }
  }

  // ─── Directory bulk bar (docs/FORECASTING_V2_TABLE_STANDARD_PLAN.md) ───
  // Same TeamService calls as the single-row actions above — Archive =
  // deactivate (undo = reactivate), Delete = hard delete. The DataTable bar
  // owns its own confirm/undo UX; the row-level Archive/Trash2 icons and the
  // old requestDeactivate/requestHardDelete confirm-dialog path are for the
  // PersonPanel detail actions only now (no "type a name" extra safety on
  // hard delete to preserve — the plain kit confirm below matches its copy).
  async function bulkArchiveMembers(items: TeamMember[]) {
    for (const m of items) await TeamService.deactivate(m.id);
    await loadAll();
  }
  async function bulkUndoArchiveMembers(items: TeamMember[]) {
    for (const m of items) await TeamService.save({ ...m, active: true });
    await loadAll();
  }
  async function bulkHardDeleteMembers(items: TeamMember[]) {
    for (const m of items) await TeamService.hardDelete(m.id);
    await loadAll();
  }

  /** The edit-session model's ONE immediate (non-batched) write —
   *  clears reports-to + every team assignment right away (its own
   *  destructive confirm already gates it), only reachable while a
   *  chart edit session is open. Fixed for the whole-row-stale-save
   *  defect class (People Rebuild edit-session grant, 2026-08-17):
   *  fetches a FRESH member row right before saving instead of reading
   *  the `members` closure, which can be seconds/minutes stale by the
   *  time a long edit session gets here. */
  async function removeFromChart(memberId: string) {
    const fresh = await TeamService.list();
    const member = fresh.find((m) => m.id === memberId);
    if (!member) return;
    if (member.reportsTo) await TeamService.save({ ...member, reportsTo: '' });
    const tas = teamAssignments.filter((a) => a.teamMemberId === memberId);
    for (const ta of tas) await TeamService.removeFromTeam(ta.id);
    await loadAll();
    toast(`${memberName(memberId)} removed from chart`);
  }

  /** Create the member row immediately — no invitation, no email. First /
   *  Middle / Last are typed directly (No Full-Name Input/Write Law,
   *  2026-08-19) — no more guess-splitting a single typed string. Default
   *  name from the email local-part still goes to firstName when nothing
   *  was typed (People Rebuild grant #1). */
  async function addMemberDirect() {
    if (!inviteEmail.trim()) {
      toast('Email is required', true);
      return;
    }
    if (canManageRoles && !inviteRoleId) {
      toast('Role is required', true);
      return;
    }
    const existing = members.find((m) => m.email?.toLowerCase() === inviteEmail.toLowerCase());
    if (existing) {
      toast(`${displayName(existing)} is already a member`, true);
      return;
    }
    const localPart = inviteEmail.trim().split('@')[0];
    const firstName = inviteFirstName.trim() || localPart;
    const middleName = inviteMiddleName.trim() || undefined;
    const lastName = inviteLastName.trim() || undefined;
    const displayFallback = combineName(firstName, middleName, lastName) || localPart;
    const payload: Partial<TeamMember> = {
      id: '',
      name: displayFallback,
      firstName,
      middleName,
      lastName,
      email: inviteEmail.trim().toLowerCase(),
      active: true,
      employmentType: 'salary',
      createdAt: '',
      updatedAt: '',
    };
    if (canManageRoles && inviteRoleId) {
      const role = roles.find((r) => r.id === inviteRoleId);
      payload.role = role?.name || 'User';
      payload.roleId = inviteRoleId;
    } else {
      payload.role = 'User';
    }
    if (inviteBillingRoleId) payload.billingRoleId = inviteBillingRoleId;
    try {
      await TeamService.save(payload as TeamMember);
      setShowInviteForm(false);
      setActivePanel((p) => (p === 'addMember' ? null : p));
      setInviteEmail('');
      setInviteFirstName('');
      setInviteMiddleName('');
      setInviteLastName('');
      setInviteRoleId('');
      setInviteBillingRoleId('');
      await loadAll();
      toast(
        canManageRoles || !inviteRoleId
          ? `${displayFallback} added to the team`
          : `${displayFallback} added to the team — ask an admin to set their privilege role`
      );
    } catch (err: any) {
      toast(err.message || 'Failed to add member', true);
    }
  }

  async function inviteMember() {
    if (!inviteEmail.trim()) {
      toast('Email is required', true);
      return;
    }
    if (!inviteRoleId) {
      toast('Role is required', true);
      return;
    }
    const existing = members.find((m) => m.email?.toLowerCase() === inviteEmail.toLowerCase());
    if (existing) {
      toast(`${displayName(existing)} is already a member`, true);
      return;
    }
    const existingInv = invitations.find(
      (i) => i.email.toLowerCase() === inviteEmail.toLowerCase()
    );
    if (existingInv) {
      toast('An invitation is already pending for this email', true);
      return;
    }
    try {
      const saved = await InvitationService.save({
        id: '',
        email: inviteEmail.trim().toLowerCase(),
        // Invitation.name is a single string used only for the invite
        // email's greeting line (never written to team_members/
        // member_profiles) — deriving it from the split fields here keeps
        // the greeting accurate without a full-name INPUT anywhere.
        name: combineName(inviteFirstName, inviteMiddleName, inviteLastName) || undefined,
        roleId: inviteRoleId || undefined,
        billingRoleId: inviteBillingRoleId || undefined,
        invitedBy: authUser?.teamMemberId || '',
        status: 'pending',
        createdAt: new Date().toISOString(),
      });
      setShowInviteForm(false);
      setActivePanel((p) => (p === 'addMember' ? null : p));
      setInviteEmail('');
      setInviteFirstName('');
      setInviteMiddleName('');
      setInviteLastName('');
      setInviteRoleId('');
      setInviteBillingRoleId('');
      await loadAll();

      try {
        await InvitationService.sendEmail((saved as any).id);
        toast(`Invitation email sent to ${inviteEmail.trim()}`);
      } catch (err: any) {
        toast(`Invitation saved but email failed: ${err.message || err}`, true);
      }
    } catch (err: any) {
      toast(err.message || 'Failed to create invitation', true);
    }
  }

  async function cancelInvitation(id: string) {
    await InvitationService.cancel(id);
    await loadAll();
    toast('Invitation cancelled');
  }

  async function assignProject(projectId: string) {
    if (!editingMember?.id) return;
    await TeamService.assign(projectId, editingMember.id);
    await loadAll();
    const pa = await TeamService.listAssignments(editingMember.id);
    setMemberProjectAssignments(pa);
    setAddingProject(false);
    toast('Project assigned');
  }

  async function unassignProject(assignmentId: string) {
    if (!editingMember?.id) return;
    const assignment = memberProjectAssignments.find((a) => a.id === assignmentId);
    try {
      await TeamService.unassign(assignmentId);
      if (assignment) {
        const project = projects.find((p) => p.id === assignment.projectId);
        // A removed member can't stay PIC/PM — clear it on the record.
        if (project && (project.picId === editingMember.id || project.pmId === editingMember.id)) {
          await ProjectService.save({
            ...project,
            picId: project.picId === editingMember.id ? undefined : project.picId,
            pmId: project.pmId === editingMember.id ? undefined : project.pmId,
          });
        }
      }
      await loadAll();
      const pa = await TeamService.listAssignments(editingMember.id);
      setMemberProjectAssignments(pa);
      toast('Project removed');
    } catch (err: any) {
      toast(err.message || 'Failed to remove project', true);
    }
  }

  // ─── Team editor panel actions (Team Management UX Overhaul) ───
  // Team SELECTION happens via the Directory team tree (TeamTree.tsx);
  // "New Team" opens this same panel with a blank team.

  function openTeamEditor(t: Team) {
    setActivePanel('team');
    setEditingTeam({ ...t });
    setTeamDirty(false);
    setAddingTeamMember(false);
    setEditingTeamAssignments(t.id ? teamAssignments.filter((a) => a.teamId === t.id) : []);
  }

  /** Guarded entry point — mirrors requestOpenMember's cross-panel discard
   *  handling for the team editor. Used by both the team tree (select) and
   *  the "New Team" button (blank team). */
  function requestOpenTeam(t: Team) {
    if (activePanel === 'team' && editingTeam?.id === t.id) return;
    if (activePanel === 'team' && teamDirty) {
      setPendingOpen({ kind: 'team', team: t });
      setConfirmState({ kind: 'discardTeam' });
      return;
    }
    if (activePanel === 'person' && memberDirty) {
      setPendingOpen({ kind: 'team', team: t });
      setConfirmState({ kind: 'discardMember' });
      return;
    }
    if (activePanel === 'person') closeMemberEditorForce();
    if (activePanel === 'addMember') closeAddMemberPanelForce();
    openTeamEditor(t);
  }

  function handleNewTeamRequest() {
    requestOpenTeam(emptyTeam());
  }

  function requestCloseTeamPanel() {
    if (teamDirty) setConfirmState({ kind: 'discardTeam' });
    else closeTeamEditorForce();
  }
  function closeTeamEditorForce() {
    setEditingTeam(null);
    setTeamDirty(false);
    setEditingTeamAssignments([]);
    setAddingTeamMember(false);
    if (activePanel === 'team') setActivePanel(null);
  }

  /** Add-member panel — no dirty tracking (mirrors the old inline form's
   *  Cancel, which never confirmed either); switching away just clears the
   *  typed email/name the same way Cancel did. */
  function closeAddMemberPanelForce() {
    setShowInviteForm(false);
    setInviteEmail('');
    setInviteFirstName('');
    setInviteMiddleName('');
    setInviteLastName('');
    if (activePanel === 'addMember') setActivePanel(null);
  }

  function requestOpenAddMemberPanel() {
    if (activePanel === 'addMember') return;
    if (activePanel === 'person' && memberDirty) {
      setPendingOpen({ kind: 'addMember' });
      setConfirmState({ kind: 'discardMember' });
      return;
    }
    if (activePanel === 'team' && teamDirty) {
      setPendingOpen({ kind: 'addMember' });
      setConfirmState({ kind: 'discardTeam' });
      return;
    }
    if (activePanel === 'person') closeMemberEditorForce();
    if (activePanel === 'team') closeTeamEditorForce();
    setActivePanel('addMember');
    setShowInviteForm(true);
  }

  /** Team drag-drop from the Directory team tree — reuses the existing
   *  team-assignment API directly (Directory has no session model). */
  async function dropMemberOnTeamFromTree(memberId: string, teamId: string) {
    await handleTeamAssign(memberId, teamId);
    if (editingTeam?.id === teamId) {
      setEditingTeamAssignments(await TeamService.listTeamAssignments(teamId));
    }
    if (editingMember?.id === memberId) {
      setMemberTeamAssignments(await TeamService.listTeamAssignments(undefined, memberId));
    }
  }

  /** Reparent (nest/un-nest) from the team tree — mirrors the cycle-safety
   *  guard the old TeamsPanel parent picker used (never allow a team to
   *  become its own descendant); TeamTree also blocks the drop visually,
   *  this is the defensive server-write-side check. */
  async function reparentTeamFromTree(teamId: string, newParentId: string | undefined) {
    const team = teams.find((t) => t.id === teamId);
    if (!team) return;
    if (newParentId) {
      const desc = new Set<string>();
      const queue = teams.filter((t) => t.parentTeamId === teamId);
      while (queue.length) {
        const d = queue.pop()!;
        desc.add(d.id);
        queue.push(...teams.filter((c) => c.parentTeamId === d.id));
      }
      if (newParentId === teamId || desc.has(newParentId)) {
        toast('Cannot nest a team inside itself or one of its own sub-teams', true);
        return;
      }
    }
    try {
      const saved = await TeamService.saveTeam({ ...team, parentTeamId: newParentId });
      await loadAll();
      if (editingTeam?.id === teamId) setEditingTeam({ ...saved });
      toast(
        newParentId
          ? `${team.name} nested under ${teamName(newParentId)}`
          : `${team.name} un-nested`
      );
    } catch (err: any) {
      toast(err.message || 'Failed to move team', true);
    }
  }

  function updateTeam(updates: Partial<Team>) {
    setEditingTeam((prev) => (prev ? { ...prev, ...updates } : prev));
    setTeamDirty(true);
  }

  async function saveTeam() {
    if (!editingTeam) return;
    if (!editingTeam.name.trim()) {
      toast('Team name is required', true);
      return;
    }
    try {
      const saved = await TeamService.saveTeam(editingTeam);
      await loadAll();
      setEditingTeam({ ...saved });
      setTeamDirty(false);
      toast('Team saved');
    } catch (err: any) {
      toast(err.message || 'Save failed', true);
    }
  }

  function requestDeleteTeam(id: string) {
    setConfirmState({ kind: 'deleteTeam', id });
  }

  async function performDeleteTeam(id: string) {
    setConfirmBusy(true);
    try {
      await TeamService.deleteTeam(id);
      if (editingTeam?.id === id) closeTeamEditorForce();
      await loadAll();
      toast('Team deleted');
      setConfirmState(null);
    } catch (err: any) {
      toast(err.message || 'Delete failed', true);
    } finally {
      setConfirmBusy(false);
    }
  }

  async function addMemberToTeam(memberId: string, role: 'leader' | 'member') {
    if (!editingTeam?.id) return;
    try {
      await TeamService.assignToTeam(editingTeam.id, memberId, role);
      await loadAll();
      const ta = await TeamService.listTeamAssignments(editingTeam.id);
      setEditingTeamAssignments(ta);
      toast('Member added to team');
    } catch (err: any) {
      toast(err.message || 'Failed to add member', true);
    }
  }

  async function removeMemberFromTeam(assignmentId: string) {
    if (!editingTeam?.id) return;
    await TeamService.removeFromTeam(assignmentId);
    await loadAll();
    const ta = await TeamService.listTeamAssignments(editingTeam.id);
    setEditingTeamAssignments(ta);
    toast('Member removed from team');
  }

  async function changeTeamMemberRole(assignmentId: string, newRole: 'leader' | 'member') {
    if (!editingTeam?.id) return;
    const ta = editingTeamAssignments.find((a) => a.id === assignmentId);
    if (!ta) return;
    await TeamService.updateTeamAssignment({ ...ta, teamRole: newRole });
    await loadAll();
    const updated = await TeamService.listTeamAssignments(editingTeam.id);
    setEditingTeamAssignments(updated);
  }

  // ─── Computed ───

  const activeCount = members.filter((m) => m.active).length;

  const assignedMemberIds = new Set(editingTeamAssignments.map((ta) => ta.teamMemberId));
  const availableTeamMembers = members.filter((m) => !assignedMemberIds.has(m.id));

  const assignedProjectIds = new Set(memberProjectAssignments.map((a) => a.projectId));
  const availableProjects = projects
    .filter((p) => !assignedProjectIds.has(p.id))
    .sort((a, b) => (a.projectNumber || '').localeCompare(b.projectNumber || ''));

  const assignedTeamIdsForMember = new Set(memberTeamAssignments.map((ta) => ta.teamId));
  const availableTeamsForMember = teams.filter((t) => !assignedTeamIdsForMember.has(t.id));

  // ─── Confirm dialog copy ───
  const confirmCopy = (() => {
    switch (confirmState?.kind) {
      case 'discardMember':
      case 'discardTeam':
        return {
          title: 'Discard unsaved changes?',
          description: 'Your changes will be lost.',
          confirmLabel: 'Discard',
        };
      case 'deleteTeam':
        return {
          title: 'Delete this team?',
          description:
            "Members will not be deleted — they stay on the roster, they're just no longer grouped under this team.",
          confirmLabel: 'Delete team',
        };
      case 'removeMember':
        return confirmState.hard
          ? {
              title: 'Delete team member permanently?',
              description:
                'This permanently deletes their record and login — it cannot be undone. To just remove their access and keep their history, archive instead.',
              confirmLabel: 'Delete permanently',
            }
          : {
              title: 'Archive team member?',
              description:
                "They'll lose access immediately and be removed from every team and the org chart. Their record and history stay intact — you can restore them later.",
              confirmLabel: 'Archive',
            };
      default:
        return { title: '', description: '', confirmLabel: 'Confirm' };
    }
  })();

  /** Resolves a pending cross-panel open (§ single-slot right panel) after
   *  its discard confirm — opens whatever was requested (another member,
   *  another team, or the add-member panel) in place of just closing. */
  function resolvePendingOpen() {
    const target = pendingOpen;
    setPendingOpen(null);
    if (target?.kind === 'member') openPersonPanel(target.member);
    else if (target?.kind === 'team') openTeamEditor(target.team);
    else if (target?.kind === 'addMember') {
      setActivePanel('addMember');
      setShowInviteForm(true);
    }
  }

  function handleConfirm() {
    if (!confirmState) return;
    if (confirmState.kind === 'discardMember') {
      const held = blockedBackRef.current;
      blockedBackRef.current = null;
      closeMemberEditorForce();
      setConfirmState(null);
      held?.proceed();
      resolvePendingOpen();
    } else if (confirmState.kind === 'discardTeam') {
      closeTeamEditorForce();
      setConfirmState(null);
      resolvePendingOpen();
    } else if (confirmState.kind === 'removeMember') {
      performRemoveMember(confirmState.id, confirmState.hard);
    } else if (confirmState.kind === 'deleteTeam') {
      performDeleteTeam(confirmState.id);
    }
  }

  // Address: the person open in the panel; Back from it closes the panel.
  useAddressSync({
    address:
      activePanel === 'person' && editingMember?.id
        ? toAddress({ view: 'team', memberId: editingMember.id })
        : null,
    listPath: '/people',
    onLeave: requestCloseMemberEditor,
    guard: {
      when: activePanel === 'person' && memberDirty,
      onBlocked: (proceed, reset) => {
        blockedBackRef.current = { proceed, reset };
        setConfirmState({ kind: 'discardMember' });
      },
    },
    title:
      activePanel === 'person' && editingMember
        ? [editingMember.firstName, editingMember.lastName].filter(Boolean).join(' ') || null
        : null,
  });

  // ─── Render ───

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Page header */}
      <div style={{ padding: `${rem(20)} var(--space-5) 0`, flexShrink: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--space-4)',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: 700,
                margin: 0,
                letterSpacing: '-0.01em',
              }}
            >
              People
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-base)',
                color: 'var(--tm)',
                margin: 'var(--space-1) 0 0',
              }}
            >
              {activeCount} active member{activeCount !== 1 ? 's' : ''} &middot; {teams.length} team
              {teams.length !== 1 ? 's' : ''}
              {authUser?.firmSlug && canCreateMembers && (
                <>
                  {' '}
                  &middot; Company Code{' '}
                  <span
                    style={{
                      cursor: 'pointer',
                      userSelect: 'all',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      padding: `${rem(1)} ${rem(7)}`,
                      borderRadius: 'var(--radius-md)',
                      background: 'color-mix(in srgb, var(--ac) 10%, transparent)',
                      border: '1px solid color-mix(in srgb, var(--ac) 22%, transparent)',
                      color: 'var(--ac)',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-base)',
                      verticalAlign: 'baseline',
                    }}
                    title="Click to copy"
                    onClick={() => {
                      navigator.clipboard.writeText(authUser.firmSlug);
                      toast('Company code copied!');
                    }}
                  >
                    {authUser.firmSlug}
                    <Icon icon={Copy} em={0.8462} aria-hidden="true" />
                  </span>
                </>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {canEditMembers && (
              <Button variant="secondary" className="gap-1.5" onClick={handleNewTeamRequest}>
                <PlusIcon />
                New Team
              </Button>
            )}
            {canCreateMembers && (
              <Button
                className="gap-1.5"
                onClick={() => {
                  if (tab === 'orgchart' && orgChartDirtyRef.current) {
                    toast('Save or cancel your Org Chart changes before adding a member', true);
                    return;
                  }
                  safeSetTab('directory');
                  const ur = roles.find((r) => r.name?.toLowerCase() === 'user');
                  if (canManageRoles && ur && !inviteRoleId) setInviteRoleId(ur.id);
                  requestOpenAddMemberPanel();
                }}
              >
                <PlusIcon />
                Add Member
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={(v) => safeSetTab(v as ActiveTab)}>
          <TabsList>
            <TabsTrigger value="directory">Directory</TabsTrigger>
            <TabsTrigger value="orgchart">Org Chart</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab content */}
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          padding: 'var(--space-4) var(--space-5) var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {tab === 'directory' && (
          <DirectoryTab
            loading={loading && members.length === 0}
            stale={stale}
            onRetry={() => loadAll(true)}
            invitations={invitations}
            onCancelInvitation={cancelInvitation}
            activePanel={activePanel}
            showAddMemberPanel={showInviteForm}
            inviteEmail={inviteEmail}
            inviteFirstName={inviteFirstName}
            inviteMiddleName={inviteMiddleName}
            inviteLastName={inviteLastName}
            inviteRoleId={inviteRoleId}
            inviteBillingRoleId={inviteBillingRoleId}
            setInviteEmail={setInviteEmail}
            setInviteFirstName={setInviteFirstName}
            setInviteMiddleName={setInviteMiddleName}
            setInviteLastName={setInviteLastName}
            setInviteRoleId={setInviteRoleId}
            setInviteBillingRoleId={setInviteBillingRoleId}
            onCloseAddMemberPanel={closeAddMemberPanelForce}
            addMemberDirect={addMemberDirect}
            inviteMember={inviteMember}
            members={members}
            teams={teams}
            teamAssignments={teamAssignments}
            roles={roles}
            billingRoles={billingRoles}
            departments={departments}
            allAssignments={allAssignments}
            roleName={roleName}
            teamName={teamName}
            canViewRoles={canViewRoles}
            canViewBilling={canViewBilling}
            canEditMembers={canEditMembers}
            canRemoveMembers={canRemoveMembers}
            canHardDeleteMembers={canHardDeleteMembers}
            canManageRoles={canManageRoles}
            permissions={authUser?.permissions}
            onBulkArchive={bulkArchiveMembers}
            onBulkUndoArchive={bulkUndoArchiveMembers}
            onBulkHardDelete={bulkHardDeleteMembers}
            selectedMember={editingMember}
            onRowClick={requestOpenMember}
            memberDirty={memberDirty}
            memberProjectAssignments={memberProjectAssignments}
            memberTeamAssignments={memberTeamAssignments}
            availableProjects={availableProjects}
            availableTeamsForMember={availableTeamsForMember}
            addingProject={addingProject}
            projects={projects}
            canSeeRates={hasPermission(authUser?.permissions, 'team_members.view_details')}
            canHardDeleteMember={canHardDeleteMembers}
            onUpdateMember={updateMember}
            onSaveMember={saveMember}
            onDeactivateMember={requestDeactivate}
            onReactivateMember={reactivateMember}
            onHardDeleteMember={requestHardDelete}
            onClosePanel={requestCloseMemberEditor}
            onAssignProject={assignProject}
            onUnassignProject={unassignProject}
            onToggleAddProject={() => setAddingProject((v) => !v)}
            onAddTeamToMember={addTeamToMember}
            onRemoveTeamFromMember={removeTeamFromMember}
            onToggleMemberTeamRole={toggleMemberTeamRole}
            memberName={memberName}
            editingTeam={editingTeam}
            teamDirty={teamDirty}
            editingTeamAssignments={editingTeamAssignments}
            availableTeamMembers={availableTeamMembers}
            addingTeamMember={addingTeamMember}
            onSelectTeam={requestOpenTeam}
            onUpdateTeam={updateTeam}
            onSaveTeam={saveTeam}
            onDeleteTeam={requestDeleteTeam}
            onAddMemberToTeam={addMemberToTeam}
            onRemoveMemberFromTeam={removeMemberFromTeam}
            onChangeTeamMemberRole={changeTeamMemberRole}
            onToggleAddTeamMember={() => setAddingTeamMember((v) => !v)}
            onCloseTeamPanel={requestCloseTeamPanel}
            onDropMemberOnTeam={dropMemberOnTeamFromTree}
            onReparentTeam={reparentTeamFromTree}
          />
        )}

        {tab === 'orgchart' && (
          <OrgChartV2
            canViewBilling={canViewBilling}
            canViewRoles={canViewRoles}
            members={members}
            teams={teams}
            teamAssignments={teamAssignments}
            departments={departments}
            roles={roles}
            billingRoles={billingRoles}
            roleName={roleName}
            onMemberClick={openPersonPanel}
            onTeamAssign={handleTeamAssign}
            onTeamRemove={handleTeamRemove}
            onToggleTeamRole={handleToggleTeamRole}
            onRemoveFromChart={removeFromChart}
            onDeleteMember={(id) => requestDeactivate(id)}
            orgChartEdges={orgChartEdges}
            orgChartJunctions={orgChartJunctions}
            orgChartNotes={orgChartNotes}
            orgChartViews={orgChartViews}
            orgChartViewPositions={orgChartViewPositions}
            currentTeamMemberId={authUser?.teamMemberId}
            isViewAdmin={canHardDeleteMembers}
            refreshAll={() => loadAll()}
            toast={toast}
            canEdit={canEditMembers}
            onDirtyChange={handleOrgChartDirtyChange}
          />
        )}
      </div>

      {/* Kit confirm dialog — every destructive/dirty-state confirm on this
          page funnels through this one instance. */}
      <ConfirmDialog
        open={confirmState !== null}
        onOpenChange={(o) => {
          if (o) return;
          setConfirmState(null);
          // Browser Back was held for this prompt: staying means cancel the move.
          setTimeout(() => {
            const b = blockedBackRef.current;
            blockedBackRef.current = null;
            b?.reset();
          }, 0);
        }}
        busy={confirmBusy}
        title={confirmCopy.title}
        description={confirmCopy.description}
        confirmLabel={confirmCopy.confirmLabel}
        onConfirm={handleConfirm}
      />
    </div>
  );
}

(window as any).PeoplePage = PeoplePage;
export { PeoplePage };
